import React, { useState } from 'react';
import { FormField, FieldType, SelectOption } from '../../../types/form';
import { X, Plus, GripVertical, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';
import { ConditionalLogicBuilder } from './ConditionalLogicBuilder';

interface FieldBuilderProps {
  field: FormField;
  onUpdate: (field: FormField) => void;
  onDelete: () => void;
  isDragging?: boolean;
  dragHandleListeners?: any;
  allFields?: FormField[];
}

export const FieldBuilder: React.FC<FieldBuilderProps> = ({
  field,
  onUpdate,
  onDelete,
  isDragging = false,
  dragHandleListeners,
  allFields = [],
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showConditional, setShowConditional] = useState(false);

  const fieldTypes: { value: FieldType; label: string }[] = [
    { value: 'text', label: 'Text' },
    { value: 'email', label: 'Email' },
    { value: 'tel', label: 'Phone' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'textarea', label: 'Textarea' },
    { value: 'select', label: 'Select' },
    { value: 'radio', label: 'Radio' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'option', label: 'Option Picker' },
    { value: 'file', label: 'File Upload' },
    { value: 'signature', label: 'Signature' },
    { value: 'calculated', label: 'Calculated Field' },
  ];

  const handleAddOption = () => {
    const newOption: SelectOption = { label: '', value: '' };
    onUpdate({
      ...field,
      options: [...(field.options || []), newOption],
    });
  };

  const handleUpdateOption = (index: number, key: 'label' | 'value', value: string) => {
    const updatedOptions = [...(field.options || [])];
    updatedOptions[index] = { ...updatedOptions[index], [key]: value };
    onUpdate({ ...field, options: updatedOptions });
  };

  const handleRemoveOption = (index: number) => {
    const updatedOptions = field.options?.filter((_, i) => i !== index) || [];
    onUpdate({ ...field, options: updatedOptions });
  };

  const needsOptions = ['select', 'radio', 'checkbox', 'option'].includes(field.type);

  return (
    <motion.div
      layout
      className={`theme-card rounded-lg border-2 p-4 space-y-4 ${
        isDragging ? 'border-blue-500 shadow-lg' : field.hidden ? 'border-purple-300 opacity-75' : 'theme-border'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className="cursor-grab active:cursor-grabbing pt-2"
          {...dragHandleListeners}
        >
          <GripVertical className="w-5 h-5 text-gray-400" />
        </div>

        <div className="flex-1 space-y-4">
          {/* Hidden field badge */}
          {field.hidden && (
            <div className="flex items-center gap-1 text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded w-fit">
              <EyeOff className="w-3 h-3" />
              Hidden field — not visible to users
            </div>
          )}
          {/* Field Type & Label */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-1">
                Field Type
              </label>
              <select
                value={field.type}
                onChange={(e) => onUpdate({ ...field, type: e.target.value as FieldType })}
                className="w-full px-3 py-2 theme-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none theme-bg-primary theme-text-primary"
              >
                {fieldTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium theme-text-primary mb-1">
                Label <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={field.label || ''}
                onChange={(e) => onUpdate({ ...field, label: e.target.value })}
                className="w-full px-3 py-2 theme-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none theme-bg-primary theme-text-primary"
                placeholder="Enter field label"
              />
            </div>
          </div>

          {/* Field Name & Placeholder */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-1">
                Field Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={field.name || ''}
                onChange={(e) => onUpdate({ ...field, name: e.target.value })}
                className="w-full px-3 py-2 theme-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none theme-bg-primary theme-text-primary"
                placeholder="field_name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium theme-text-primary mb-1">
                Placeholder
              </label>
              <input
                type="text"
                value={field.placeholder || ''}
                onChange={(e) => onUpdate({ ...field, placeholder: e.target.value })}
                className="w-full px-3 py-2 theme-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none theme-bg-primary theme-text-primary"
                placeholder="Enter placeholder text"
              />
            </div>
          </div>

          {/* Default Value */}
          {field.type !== 'file' && field.type !== 'signature' && field.type !== 'calculated' && (
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-1">
                Default Value
              </label>
              <input
                type="text"
                value={field.defaultValue || ''}
                onChange={(e) => onUpdate({ ...field, defaultValue: e.target.value })}
                className="w-full px-3 py-2 theme-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none theme-bg-primary theme-text-primary"
                placeholder="Default value (optional)"
              />
            </div>
          )}

          {/* Required & Hidden Toggles */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id={`required-${field.id}`}
                checked={field.required}
                onChange={(e) => onUpdate({ ...field, required: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <label htmlFor={`required-${field.id}`} className="text-sm font-medium theme-text-primary">
                Required field
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id={`hidden-${field.id}`}
                checked={!!field.hidden}
                onChange={(e) => onUpdate({ ...field, hidden: e.target.checked })}
                className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
              />
              <label htmlFor={`hidden-${field.id}`} className="text-sm font-medium theme-text-primary">
                Hidden field
              </label>
            </div>
          </div>

          {/* Options for select/radio/checkbox/option */}
          {needsOptions && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium theme-text-primary">Options</label>
                <button
                  type="button"
                  onClick={handleAddOption}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Option
                </button>
              </div>

              {field.options?.map((option, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={option.label || ''}
                    onChange={(e) => handleUpdateOption(index, 'label', e.target.value)}
                    placeholder="Label"
                    className="flex-1 px-3 py-2 theme-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm theme-bg-primary theme-text-primary"
                  />
                  <input
                    type="text"
                    value={option.value || ''}
                    onChange={(e) => handleUpdateOption(index, 'value', e.target.value)}
                    placeholder="Value"
                    className="flex-1 px-3 py-2 theme-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm theme-bg-primary theme-text-primary"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveOption(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Advanced Validation */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm theme-text-secondary hover:theme-text-primary"
            >
              {showAdvanced ? '− Hide' : '+ Show'} Advanced Options
            </button>

            {showAdvanced && (
              <div className="mt-3 space-y-4 p-4 theme-bg-secondary rounded-lg">
                {/* Validation Rules */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium theme-text-primary">Validation Rules</h4>

                  {(field.type === 'text' || field.type === 'textarea' || field.type === 'email') && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium theme-text-secondary mb-1">
                          Min Length
                        </label>
                        <input
                          type="number"
                          value={field.validation?.minLength || ''}
                          onChange={(e) =>
                            onUpdate({
                              ...field,
                              validation: {
                                ...field.validation,
                                minLength: e.target.value ? parseInt(e.target.value) : undefined,
                              },
                            })
                          }
                          className="w-full px-2 py-1 theme-border rounded text-sm theme-bg-primary theme-text-primary"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium theme-text-secondary mb-1">
                          Max Length
                        </label>
                        <input
                          type="number"
                          value={field.validation?.maxLength || ''}
                          onChange={(e) =>
                            onUpdate({
                              ...field,
                              validation: {
                                ...field.validation,
                                maxLength: e.target.value ? parseInt(e.target.value) : undefined,
                              },
                            })
                          }
                          className="w-full px-2 py-1 theme-border rounded text-sm theme-bg-primary theme-text-primary"
                          placeholder="100"
                        />
                      </div>
                    </div>
                  )}

                  {field.type === 'number' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Min Value
                        </label>
                        <input
                          type="number"
                          value={field.validation?.min || ''}
                          onChange={(e) =>
                            onUpdate({
                              ...field,
                              validation: {
                                ...field.validation,
                                min: e.target.value ? parseFloat(e.target.value) : undefined,
                              },
                            })
                          }
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Max Value
                        </label>
                        <input
                          type="number"
                          value={field.validation?.max || ''}
                          onChange={(e) =>
                            onUpdate({
                              ...field,
                              validation: {
                                ...field.validation,
                                max: e.target.value ? parseFloat(e.target.value) : undefined,
                              },
                            })
                          }
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </div>
                    </div>
                  )}

                  {field.type === 'file' && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Max File Size (MB)
                        </label>
                        <input
                          type="number"
                          value={field.validation?.fileMaxSize || ''}
                          onChange={(e) =>
                            onUpdate({
                              ...field,
                              validation: {
                                ...field.validation,
                                fileMaxSize: e.target.value ? parseFloat(e.target.value) : undefined,
                              },
                            })
                          }
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="5"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Allowed File Types (comma-separated)
                        </label>
                        <input
                          type="text"
                          value={field.validation?.fileTypes?.join(',') || ''}
                          onChange={(e) =>
                            onUpdate({
                              ...field,
                              validation: {
                                ...field.validation,
                                fileTypes: e.target.value ? e.target.value.split(',').map(t => t.trim()) : undefined,
                              },
                            })
                          }
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="image/png,image/jpeg,application/pdf"
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Pattern (Regex)
                    </label>
                    <input
                      type="text"
                      value={field.validation?.pattern || ''}
                      onChange={(e) =>
                        onUpdate({
                          ...field,
                          validation: { ...field.validation, pattern: e.target.value },
                        })
                      }
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      placeholder="^[A-Za-z]+$"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Pattern Error Message
                    </label>
                    <input
                      type="text"
                      value={field.validation?.patternMessage || ''}
                      onChange={(e) =>
                        onUpdate({
                          ...field,
                          validation: { ...field.validation, patternMessage: e.target.value },
                        })
                      }
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      placeholder="Custom error message"
                    />
                  </div>
                </div>

                {/* Field Width */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Field Width (Responsive)
                  </label>
                  <select
                    value={field.width || 'full'}
                    onChange={(e) => onUpdate({ ...field, width: e.target.value as any })}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value="full">Full Width</option>
                    <option value="half">Half Width (50%)</option>
                    <option value="third">Third Width (33%)</option>
                  </select>
                </div>

                {/* Help Text */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Help Text
                  </label>
                  <input
                    type="text"
                    value={field.helpText || ''}
                    onChange={(e) => onUpdate({ ...field, helpText: e.target.value })}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="Additional help text for users"
                  />
                </div>

                {/* Calculation Formula (for calculated fields) */}
                {field.type === 'calculated' && (
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Calculation Formula
                    </label>
                    <input
                      type="text"
                      value={field.calculation?.formula || ''}
                      onChange={(e) =>
                        onUpdate({
                          ...field,
                          calculation: {
                            ...field.calculation,
                            formula: e.target.value,
                            fields: field.calculation?.fields || [],
                          },
                        })
                      }
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      placeholder="e.g., field1 * field2 or field1 + field2"
                    />
                    <label className="block text-xs font-medium text-gray-600 mb-1 mt-2">
                      Field Names (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={field.calculation?.fields?.join(',') || ''}
                      onChange={(e) =>
                        onUpdate({
                          ...field,
                          calculation: {
                            ...field.calculation,
                            formula: field.calculation?.formula || '',
                            fields: e.target.value.split(',').map(f => f.trim()).filter(f => f),
                          },
                        })
                      }
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      placeholder="e.g., quantity, unitPrice"
                    />
                    <label className="block text-xs font-medium text-gray-600 mb-1 mt-2">
                      Decimal Places
                    </label>
                    <input
                      type="number"
                      value={field.calculation?.decimals ?? 2}
                      onChange={(e) =>
                        onUpdate({
                          ...field,
                          calculation: {
                            ...field.calculation,
                            formula: field.calculation?.formula || '',
                            fields: field.calculation?.fields || [],
                            decimals: e.target.value ? parseInt(e.target.value) : 2,
                          },
                        })
                      }
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      min="0"
                      max="10"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Conditional Logic */}
          <div>
            <button
              type="button"
              onClick={() => setShowConditional(!showConditional)}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              {showConditional ? '− Hide' : '+ Show'} Conditional Logic
            </button>

            {showConditional && (
              <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                <ConditionalLogicBuilder
                  field={field}
                  allFields={allFields}
                  onUpdate={onUpdate}
                />
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={onDelete}
          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </motion.div>
  );
};
